import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { 
  Settings, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  Eye, 
  Save,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  X,
  Target,
  Activity,
  Users,
  MessageSquare,
  Calendar,
  FileText,
  Database,
  Server,
  Shield,
  Bell,
  Download,
  Upload,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  Copy,
  TestTube,
  BarChart3,
  TrendingUp,
  PieChart,
  Timer,
  Zap,
  AlertCircle,
  CheckSquare,
  Square,
  Star,
  Award,
  TrendingDown,
  Percent,
  DollarSign,
  Building,
  UserCheck,
  UserX,
  Clock3,
  Clock4,
  Clock5,
  Clock6,
  Clock7,
  Clock8,
  Clock9,
  Clock10,
  Clock11,
  Clock12,
  Send,
  Paperclip,
  Image,
  Link,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  Quote,
  Code,
  Palette,
  Type,
  Layout,
  Monitor,
  Smartphone,
  Tablet,
  Key,
  Lock,
  Unlock,
  Globe,
  Wifi,
  HardDrive,
  Cpu,
  Network,
  Mail,
  Phone,
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  Sun,
  Moon,
  Cloud,
  CloudRain,
  CloudSnow,
  Wind,
  Thermometer,
  Droplets,
  Gauge,
  Zap as ZapIcon,
  Battery,
  WifiOff,
  Signal,
  SignalZero,
  SignalLow,
  SignalMedium,
  SignalHigh,
  SignalMax
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const SystemConfigurationManagement = () => {
  const [configData, setConfigData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedConfigs, setExpandedConfigs] = useState(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    const fetchConfigData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getSystemConfigurationManagement();
        if (response.success) {
          setConfigData(response.data);
        } else {
          setError('Failed to load system configuration data');
        }
      } catch (err) {
        setError('Error loading system configuration data');
        console.error('System configuration data error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfigData();
  }, []);

  const handleCreateConfig = () => {
    setSelectedConfig(null);
    setShowConfigForm(true);
  };

  const handleEditConfig = (config) => {
    setSelectedConfig(config);
    setShowConfigForm(true);
  };

  const handleCloseConfigForm = () => {
    setShowConfigForm(false);
    setSelectedConfig(null);
    setHasUnsavedChanges(false);
  };

  const handleSubmitConfig = (configData) => {
    //console.log('Submitting config:', configData);
    // In a real app, this would call an API to create/update config
    setShowConfigForm(false);
    setSelectedConfig(null);
    setHasUnsavedChanges(false);
  };

  const handleDeleteConfig = (configId) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      //console.log('Deleting config:', configId);
      // In a real app, this would call an API to delete config
    }
  };

  const handleResetConfig = (configId) => {
    if (window.confirm('Are you sure you want to reset this configuration to default?')) {
      //console.log('Resetting config:', configId);
      // In a real app, this would call an API to reset config
    }
  };

  const handleTestConfig = (configId) => {
    //console.log('Testing config:', configId);
    // In a real app, this would call an API to test the config
  };

  const handleSaveAll = () => {
    //console.log('Saving all configurations');
    // In a real app, this would call an API to save all configs
    setHasUnsavedChanges(false);
  };

  const handleResetAll = () => {
    if (window.confirm('Are you sure you want to reset all configurations to default?')) {
      //console.log('Resetting all configurations');
      // In a real app, this would call an API to reset all configs
    }
  };

  const toggleConfigExpansion = (configId) => {
    const newExpanded = new Set(expandedConfigs);
    if (newExpanded.has(configId)) {
      newExpanded.delete(configId);
    } else {
      newExpanded.add(configId);
    }
    setExpandedConfigs(newExpanded);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading system configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!configData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No system configuration data available</p>
        </div>
      </div>
    );
  }

  const { 
    systemConfigurationManagement, 
    configStats, 
    recentChanges, 
    configAnalytics
  } = configData;

  const { configurations, categories, backups, logs } = systemConfigurationManagement;

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'performance', label: 'Performance', icon: Gauge },
    { id: 'integrations', label: 'Integrations', icon: Network },
    { id: 'backups', label: 'Backups', icon: HardDrive },
    { id: 'logs', label: 'Logs', icon: FileText }
  ];

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'general': return Settings;
      case 'security': return Shield;
      case 'performance': return Gauge;
      case 'integrations': return Network;
      case 'email': return Mail;
      case 'database': return Database;
      case 'system': return Server;
      default: return Settings;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'general': return 'blue';
      case 'security': return 'red';
      case 'performance': return 'green';
      case 'integrations': return 'purple';
      case 'email': return 'orange';
      case 'database': return 'indigo';
      case 'system': return 'gray';
      default: return 'blue';
    }
  };

  const filteredConfigs = configurations.filter(config => {
    const matchesSearch = config.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         config.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || config.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Settings className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  System Configuration
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Manage system settings and configurations
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {hasUnsavedChanges && (
                <div className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900 rounded-md">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Unsaved Changes</span>
                </div>
              )}
              <button
                onClick={handleSaveAll}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
              >
                <Save className="h-4 w-4" />
                <span>Save All</span>
              </button>
              <button
                onClick={handleResetAll}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Reset All</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Config Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Settings className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Configs</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{configStats.totalConfigs}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Configs</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{configStats.activeConfigs}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Activity className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Changes Today</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{configStats.changesToday}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <HardDrive className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Backups</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{configStats.totalBackups}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Configuration Sections</h3>
                <nav className="space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              {/* General Tab */}
              {activeTab === 'general' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">General Settings</h2>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                        <RefreshCw className="h-4 w-4" />
                        <span>Refresh</span>
                      </button>
                    </div>
                  </div>

                  {/* Search and Filters */}
                  <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search configurations..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="all">All Categories</option>
                        <option value="general">General</option>
                        <option value="security">Security</option>
                        <option value="performance">Performance</option>
                        <option value="integrations">Integrations</option>
                        <option value="email">Email</option>
                        <option value="database">Database</option>
                        <option value="system">System</option>
                      </select>
                    </div>
                  </div>

                  {/* Configurations List */}
                  <div className="space-y-3">
                    {filteredConfigs.map((config) => {
                      const CategoryIcon = getCategoryIcon(config.category);
                      const isExpanded = expandedConfigs.has(config.id);
                      
                      return (
                        <div key={config.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center space-x-4">
                              <button
                                onClick={() => toggleConfigExpansion(config.id)}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-gray-400" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-400" />
                                )}
                              </button>
                              <div className={`p-2 bg-${getCategoryColor(config.category)}-100 dark:bg-${getCategoryColor(config.category)}-900 rounded-lg`}>
                                <CategoryIcon className={`h-4 w-4 text-${getCategoryColor(config.category)}-600 dark:text-${getCategoryColor(config.category)}-400`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{config.name}</h3>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{config.category}</span>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{config.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                config.status === 'active' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                config.status === 'inactive' ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                                'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                              }`}>
                                {config.status}
                              </span>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => handleTestConfig(config.id)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                  title="Test Configuration"
                                >
                                  <TestTube className="h-4 w-4 text-gray-400" />
                                </button>
                                <button
                                  onClick={() => handleEditConfig(config)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                >
                                  <Edit3 className="h-4 w-4 text-gray-400" />
                                </button>
                                <button
                                  onClick={() => handleResetConfig(config.id)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                  title="Reset to Default"
                                >
                                  <RefreshCw className="h-4 w-4 text-gray-400" />
                                </button>
                                <button
                                  onClick={() => handleDeleteConfig(config.id)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                >
                                  <Trash2 className="h-4 w-4 text-gray-400" />
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Expanded Configuration Details */}
                          {isExpanded && (
                            <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Configuration Details</h4>
                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Type:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{config.type}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Default Value:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{config.defaultValue}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Current Value:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{config.currentValue}</span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Usage Information</h4>
                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Last Modified:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{new Date(config.lastModified).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Modified By:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{config.modifiedBy}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 dark:text-gray-400">Required:</span>
                                      <span className="text-gray-900 dark:text-gray-100">{config.required ? 'Yes' : 'No'}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Description</h4>
                                <p className="text-sm text-gray-700 dark:text-gray-300">{config.longDescription}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Other tabs would be implemented similarly */}
              {activeTab === 'security' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Security Settings</h2>
                    <Button onClick={() => setShowConfigForm(true)} className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Configure Security
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Authentication Settings */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="h-5 w-5" />
                          Authentication Settings
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Two-Factor Authentication</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Require 2FA for all users</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Enabled
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Password Policy</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Minimum 8 characters, mixed case</p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            Active
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Session Timeout</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Auto-logout after 30 minutes</p>
                          </div>
                          <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            30 min
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Login Attempts</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Lock account after 5 failed attempts</p>
                          </div>
                          <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            5 attempts
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Access Control */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Lock className="h-5 w-5" />
                          Access Control
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">IP Whitelist</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Restrict access by IP address</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Enabled
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Role-Based Access</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Granular permission system</p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            Active
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">API Rate Limiting</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">100 requests per minute</p>
                          </div>
                          <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            100/min
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Audit Logging</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Track all user actions</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Enabled
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Data Protection */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Database className="h-5 w-5" />
                          Data Protection
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Data Encryption</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">AES-256 encryption at rest</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            AES-256
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">SSL/TLS</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">HTTPS encryption in transit</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            TLS 1.3
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Data Retention</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Auto-delete after 7 years</p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            7 years
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Backup Encryption</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Encrypted backup storage</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Enabled
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Security Monitoring */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Eye className="h-5 w-5" />
                          Security Monitoring
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Intrusion Detection</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Monitor for suspicious activity</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Active
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Security Alerts</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Real-time threat notifications</p>
                          </div>
                          <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            3 alerts
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Vulnerability Scanning</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Weekly security scans</p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            Weekly
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Compliance Check</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">GDPR, SOC2 compliance</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Compliant
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Security Statistics */}
                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Security Statistics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">99.9%</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Uptime</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">0</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Security Incidents</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">3</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Active Alerts</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">156</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Security Events</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === 'performance' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Performance Settings</h2>
                  <p className="text-gray-600 dark:text-gray-400">Performance configuration management coming soon...</p>
                </div>
              )}

              {activeTab === 'integrations' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Integration Settings</h2>
                  <p className="text-gray-600 dark:text-gray-400">Integration configuration management coming soon...</p>
                </div>
              )}

              {activeTab === 'backups' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Backup Management</h2>
                  <p className="text-gray-600 dark:text-gray-400">Backup management coming soon...</p>
                </div>
              )}

              {activeTab === 'logs' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">System Logs</h2>
                  <p className="text-gray-600 dark:text-gray-400">System logs coming soon...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Form Modal */}
      {showConfigForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedConfig ? 'Edit Configuration' : 'Create New Configuration'}
              </h3>
              <button
                onClick={handleCloseConfigForm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Configuration Name
                  </label>
                  <input
                    type="text"
                    defaultValue={selectedConfig?.name || ''}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter configuration name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <option value="general">General</option>
                    <option value="security">Security</option>
                    <option value="performance">Performance</option>
                    <option value="integrations">Integrations</option>
                    <option value="email">Email</option>
                    <option value="database">Database</option>
                    <option value="system">System</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  rows={3}
                  defaultValue={selectedConfig?.description || ''}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Enter configuration description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Configuration Value
                  </label>
                  <input
                    type="text"
                    defaultValue={selectedConfig?.currentValue || ''}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter configuration value"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="json">JSON</option>
                    <option value="url">URL</option>
                    <option value="email">Email</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="required"
                  defaultChecked={selectedConfig?.required || false}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="required" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Required Configuration
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={handleCloseConfigForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSubmitConfig({})}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                {selectedConfig ? 'Update Configuration' : 'Create Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemConfigurationManagement;
